import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/util";
import { Loader, Pill, Money, Field, FileButton, VehNo } from "@/components/common";
import Modal from "@/components/Modal";
import { PageTitle, SearchBox, Table, Td, FileInput, useDebounced } from "@/pages/admin/ui";
import DateInput from "@/components/DateInput";

function AdminReply({ v, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/violations/${v.id}/admin-reply`, { text }); toast.success("تم إرسال الرد للسائق"); setText(""); onClose(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Modal open={!!v} onClose={onClose} title="الرد على السائق" testid="admin-reply-modal">
      <p className="text-sm text-slate-500">اكتب بالعربي، وسيصل الرد للسائق مترجماً تلقائياً بلغته.</p>
      <textarea data-testid="admin-reply-input" className="field" value={text} onChange={(e) => setText(e.target.value)} placeholder="اكتب ردك هنا" />
      <button data-testid="admin-reply-send" disabled={busy || !text.trim()} onClick={send} className="btn btn-lg btn-blue w-full">إرسال الرد</button>
    </Modal>
  );
}

function CandidateList({ items, onPick }) {
  return (
    <div className="rounded-xl bg-blue-50 p-3 text-sm space-y-2" data-testid="nvio-candidates">
      <p className="font-bold">يوجد أكثر من مركبة بهذا الرقم، اختر المركبة المقصودة:</p>
      {items.map((c) => (
        <button key={c.id} type="button" data-testid={`nvio-candidate-${c.id}`} onClick={() => onPick(c.id)}
          className="w-full flex items-center justify-between gap-2 rounded-lg bg-white border p-2 text-right hover:border-blue-500">
          <VehNo adm={c.admin_number} plate={c.plate} />
          <span>{c.driver ? `${c.driver.full_name} (${c.driver.serial})` : "بدون سائق حالي"}</span>
        </button>
      ))}
    </div>
  );
}

function NewViolation({ open, onClose, onSaved }) {
  const [f, setF] = useState({ plate: "", date: "", violation_type: "", amount: "", note: "", notify: true, vehicle_id: "" });
  const [file, setFile] = useState(null);
  const [look, setLook] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, ...(k === "plate" ? { vehicle_id: "" } : {}) });
  const lookup = async (vehicle_id = "") => {
    try {
      const { data } = await api.get("/vehicles/lookup", { params: { plate: f.plate, date: f.date, vehicle_id } });
      setLook(data);
      setF((p) => ({ ...p, vehicle_id: data.found ? data.vehicle.id : "" }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, String(v)));
    if (file) fd.append("file", file);
    try {
      const { data } = await api.post("/violations", fd);
      toast.success(data.driver_id ? (f.notify ? "تم حفظ المخالفة وإرسال إشعار للسائق" : "تم حفظ المخالفة") : "تم حفظ المخالفة (لا يوجد سائق مرتبط بهذا التاريخ)");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const drv = look?.found && look.current_driver;
  return (
    <Modal open={open} onClose={onClose} title="إضافة مخالفة مرورية" testid="new-violation-modal">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="رقم المركبة (إداري أو عادي)" required><input data-testid="nvio-plate" className="field field-sm ltr text-left" value={f.plate} onChange={set("plate")} /></Field>
          <Field label="تاريخ المخالفة" required><DateInput testid="nvio-date" className="field field-sm" value={f.date} onChange={set("date")} /></Field>
        </div>
        <button type="button" data-testid="nvio-lookup" onClick={() => lookup()} className="btn btn-sm btn-dark"><Search className="w-4 h-4" />بحث عن المركبة</button>
        {look && (look.found ? (
          <div className="rounded-xl bg-amber-50 p-3 text-sm space-y-1" data-testid="nvio-lookup-result">
            <div>المركبة: <b><VehNo adm={look.vehicle.admin_number} plate={look.vehicle.plate} /></b></div>
            <div data-testid="nvio-driver-name">السائق: <b>{drv ? `${drv.full_name} (${drv.serial})` : "لا يوجد سائق مرتبط بهذه المركبة"}</b></div>
            {drv && <Link to={`/admin/drivers/${drv.id}`} className="text-blue-700 underline">فتح ملف السائق</Link>}
          </div>) : look.candidates?.length ? (
          <CandidateList items={look.candidates} onPick={(id) => lookup(id)} />
        ) : <p className="text-red-600 font-bold">لا توجد شاحنة بهذا الرقم</p>)}
        <Field label="نوع المخالفة" required><input data-testid="nvio-type" className="field field-sm" value={f.violation_type} onChange={set("violation_type")} placeholder="مثال: تجاوز السرعة" /></Field>
        <Field label="المبلغ (د.ك)" required><input data-testid="nvio-amount" className="field field-sm ltr text-left" inputMode="decimal" value={f.amount} onChange={set("amount")} /></Field>
        <Field label="صورة المخالفة أو PDF"><FileInput testid="nvio-file" onChange={setFile} /></Field>
        <Field label="ملاحظة الإدارة"><textarea data-testid="nvio-note" className="field" value={f.note} onChange={set("note")} /></Field>
        <label className="flex items-center gap-2 font-bold"><input data-testid="nvio-notify" type="checkbox" className="w-5 h-5" checked={f.notify} onChange={(e) => setF({ ...f, notify: e.target.checked })} />إرسال إشعار للسائق المعني</label>
        <button data-testid="nvio-save" disabled={busy} onClick={save} className="btn btn-lg btn-red w-full">حفظ المخالفة</button>
      </div>
    </Modal>
  );
}

export default function Violations() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [list, setList] = useState(null);
  const [add, setAdd] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const load = () => api.get("/violations", { params: { q: dq } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  const notify = async (v) => {
    try { await api.post(`/violations/${v.id}/notify`); toast.success("تم إرسال الإشعار"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageTitle title="المخالفات المرورية" icon={AlertTriangle} color="text-red-600">
        <button data-testid="add-violation-btn" onClick={() => setAdd(true)} className="btn btn-md btn-red"><Plus className="w-5 h-5" />إضافة مخالفة</button>
      </PageTitle>
      <div className="mb-4"><SearchBox value={q} onChange={setQ} placeholder="ابحث بالرقم الإداري أو العادي للمركبة أو اسم السائق أو رقمه" testid="violations-search" /></div>
      {!list ? <Loader /> : (
        <Table head={["التاريخ", "المركبة", "السائق", "النوع", "المبلغ", "المرفق", "الإشعار", "الاطلاع", "رد السائق"]} testid="violations-table">
          {list.map((v) => <tr key={v.id}>
            <Td>{fmtDate(v.date)}</Td><Td><VehNo adm={v.admin_number} plate={v.plate} /></Td>
            <Td>{v.driver_id ? <Link to={`/admin/drivers/${v.driver_id}`} className="text-blue-700">{v.driver_name} <span className="ltr">{v.driver_serial}</span></Link> : "غير محدد"}</Td>
            <Td>{v.violation_type}</Td><Td><Money fils={v.amount_fils} className="text-red-600" /></Td>
            <Td><FileButton fileId={v.file_id} label="عرض" className="btn btn-sm btn-ghost" testid={`vio-file-${v.id}`} /></Td>
            <Td>{v.notified_at ? fmtDateTime(v.notified_at) : v.driver_id ? <button data-testid={`vio-notify-${v.id}`} onClick={() => notify(v)} className="btn btn-sm btn-red">إرسال</button> : "—"}</Td>
            <Td>{v.seen_at ? <Pill color="green">اطلع {fmtDateTime(v.seen_at)}</Pill> : v.viewed_at ? <Pill color="orange" testid={`vio-viewed-${v.id}`}>شاهدها {fmtDateTime(v.viewed_at)}</Pill> : <Pill color="red">لم يشاهدها</Pill>}</Td>
            <Td className="max-w-xs">{v.replies.map((r, i) => <div key={i} className={`text-sm ${r.from === "admin" ? "text-orange-700" : ""}`}>{r.from === "admin" && <b>رد الإدارة: </b>}{r.text} <span className="text-xs text-slate-400">{fmtDateTime(r.at)}</span>{r.text_ar && <div className="text-blue-800" data-testid="reply-ar">الترجمة: {r.text_ar}</div>}</div>)}
              {v.driver_id && <button data-testid={`vio-reply-${v.id}`} onClick={() => setReplyTo(v)} className="btn btn-sm btn-ghost mt-1">رد</button>}</Td>
          </tr>)}
        </Table>
      )}
      <NewViolation open={add} onClose={() => setAdd(false)} onSaved={load} />
      <AdminReply v={replyTo} onClose={() => setReplyTo(null)} onSaved={load} />
    </div>
  );
}
